import React, { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import LogoTitle from "@Components/LogoTitle/LogoTitle";
import FormPageTemplate from "@Pages/FormPageTemplate/FormPageTemplate";
import loginLeftPage from "@Assets/BG2.jpg";
import themeCreator from "@Themes/themeCreator";
import { useParams } from "react-router-dom";
import { USER_CONFIRM_OTP, USER_FORGOT_PASS } from "@API/APIRoutes";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";
import axiosInstance from "@API/axios";
import { changePassword } from "@Routes/DefaultRoutes";
import { emailVerification } from "@Routes/DefaultRoutes";
import VerificationCodeStyles from "./VerificationCodeStyles";
import { Grid, Typography, TextField, Button, Box } from "@mui/material";
import axios from "@API/axios";


export default function VerificationCode() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const inputRefs = useRef([...Array(6)].map(() => null));

  const customTheme = themeCreator();

  const { email } = useParams();
  const [isDisabled, setIsDisabled] = useState(false);
  const [timer, setTimer] = useState(40);
  const [otpExpired, setotpExpired] = useState(false);
  const [loading, setLoading] = useState(false);

  const initialTime = sessionStorage.getItem("timer")
    ? parseInt(sessionStorage.getItem("timer"), 10)
    : 595;

  const [otpTimer, setotpTimer] = useState(initialTime);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (email == null) {
      navigate("/");
    }
  }, []);

  // time for resend
  useEffect(() => {
    let countdown;
    if (isDisabled) {
      countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev === 1) {
            clearInterval(countdown);
            setIsDisabled(false);
            return 30;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(countdown);
  }, [isDisabled]);

  //timer for otp expiry date
  useEffect(() => {
    if (otpTimer > 0) {
      sessionStorage.setItem("timer", otpTimer); // Save to session storage
      const interval = setInterval(() => {
        setotpTimer((prev) => {
          sessionStorage.setItem("timer", prev - 1); // Update session storage
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval); // Cleanup on unmount
    } else {
      setotpExpired(true);
      sessionStorage.removeItem("timer");
    }
  }, [otpTimer]);

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (value.length === 1 && index < 5) {
      inputRefs.current[index + 1].focus(); // Move to the next input
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      inputRefs.current[index - 1].focus(); // Move to the previous input on backspace
    }
  };

  const onVerifySubmit = (data) => {
    const verificationCode = Object.values(data).join(""); // Combine values into a single string
    setLoading(true);

    axios
      .get(USER_CONFIRM_OTP, { params: { otp: verificationCode } })
      .then((response) => {
        navigate(
          changePassword
            .replace(":email", encodeURIComponent(email))
            .replace(":otp", verificationCode)
        );
      })
      .catch((error) => {
        setLoading(false);
        dispatch(
          setSnackbar({
            open: true,
            message: "Invalid OTP",
            error: true,
          })
        );
      });
  };

  const handleResend = () => {
    setTimer(40);
    setIsDisabled(true);
    axiosInstance
      .get(USER_FORGOT_PASS, {
        params: { email: email },
      })
      .then((response) => {
        setotpTimer(595);
        setotpExpired(false);
        dispatch(
          setSnackbar({
            open: true,
            message: "OTP sent to your email.",
            error: false,
          })
        );
      })
      .catch((err) => {
        setTimer(40);
        setIsDisabled(false);
        dispatch(
          setSnackbar({
            open: true,
            message: "Internal Server Error.",
            error: true,
          })
        );
      });
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const rightGridContent = (
    <>
      <form onSubmit={handleSubmit(onVerifySubmit)} className="formWrapper">
        <Grid
          container
          spacing={2}
          className="inputContainer"
          justifyContent="flex-end"
        >
          <Grid item xs={12} justifyContent="flex-start">
            <LogoTitle isDisabled />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="center">
            <Typography
              variant="h4"
              className="loginTitle"
              textAlign="center"
              sx={{ mb: 1, mt: 3 }}
            >
              Verify Your Identity
            </Typography>
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="flex-start">
            <Typography
              variant="body1"
              sx={{
                fontSize: ".9rem",
                ml: 1,
                color: "gray.500",
              }}
            >
              Please Enter The OTP Sent to Your Email Address
            </Typography>
          </Grid>
          <Grid container item spacing={2} justifyContent="center">
            {[...Array(6)].map((_, index) => (
              <Grid item key={index} sx={{ width: "7.5rem", height: "5rem" }}>
                <TextField
                  variant="outlined"
                  inputProps={{
                    maxLength: 1,
                    style: { textAlign: "center", fontSize: "1.5rem" },
                  }}
                  placeholder="•"
                  {...register(`code${index}`, { required: "Required" })}
                  error={!!errors[`code${index}`]}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item xs={6} display="flex" justifyContent="flex-start">
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.1rem",
                ml: 1,
              }}
              onClick={!isDisabled ? handleResend : null} // Prevent clicking when disabled
            >
              {`OTP expires in: ${formatTime(otpTimer)}`}
            </Typography>
          </Grid>
          <Grid item xs={6} display="flex" justifyContent="flex-end">
            <Typography
              variant="body2"
              sx={{
                color: isDisabled ? "grey.500" : "primary.main", // Change color when disabled
                cursor: isDisabled ? "default" : "pointer",
                fontSize: "1.1rem",
                mr: 1,
              }}
              onClick={!isDisabled ? handleResend : null} // Prevent clicking when disabled
            >
              {isDisabled ? `Resend in ${timer}s` : "Resend Code"}
            </Typography>
          </Grid>

          <Box className="submissionBox">
            <Button
              type="submit"
              variant="contained"
              className="submissionButton"
              size="large"
              sx={{
                backgroundColor: otpExpired ? "gray.700" : "primary.main",
                cursor: otpExpired ? "not-allowed" : "pointer",
                fontSize: "1.1rem",
                mt: 1,
              }}
              disabled={otpExpired || loading}
            >
              {otpExpired ? (
                "Expired"
              ) : loading ? (
                <LoadingComponent size="2em" thickness={5} color="white" />
              ) : (
                "Verify"
              )}
            </Button>
          </Box>
        </Grid>
      </form>
    </>
  );

  return (
    <VerificationCodeStyles>
      <FormPageTemplate
        rightGridForm={rightGridContent}
        leftGridTitle="Verification Code Page"
        textColor={customTheme.palette.white.main}
        leftGridBG={loginLeftPage}
        backUrl={emailVerification} // URL to navigate back to
      />
    </VerificationCodeStyles>
  );
}
